import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Download, Loader2, UploadCloud } from "lucide-react";
import { OrgTreeSelect, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useTran } from "@/hooks";
import { JJ_GROUP_FRAMEWORK_CODE } from "@/lib/constants/frameworks.constants";
import { authSessionStore } from "@/features/auth/stores/auth-session.store";
import {
  hasParentAccess,
  normalizeOrgId,
} from "@/features/esg/utils/org-scope.utils";
import {
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import {
  filterOrgTreeByIds,
  collectSelectableOrgIds,
} from "../../utils/orgTree.helpers";
import {
  downloadBatchUploadTemplate,
  loadBatchTemplateFilterOptions,
} from "../../operations/batch-upload.ops";

const CATEGORIES = ["Environmental", "Social", "Governance", "General"];

const UploadForm = observer(
  ({
    downloadingTemplate,
    parsingFile,
    fileInputRef,
    onDragOver,
    onDrop,
    onFileChange,
  }) => {
    const { t } = useTran();
    const { filteredOrgTree } = useEntryDataStore();
    const { frameworks } = useDisclosureStore();
    const {
      batchTemplateType,
      batchTemplateOrganizationId,
      batchTemplateCategory,
      batchTemplateFramework,
      batchTemplateYear,
      batchTemplateYearOptions,
      batchTemplateOrganizationOptions,
      batchTemplateFrameworkOptions,
      batchTemplateCategoryOptions,
      loadingBatchTemplateYears,
      loadingBatchTemplateFrameworks,
      loadingBatchTemplateCategories,
      loadingDisclosures,
      setBatchTemplateType,
      setBatchTemplateOrganizationId,
      setBatchTemplateCategory,
      setBatchTemplateFramework,
      setBatchTemplateYear,
    } = useEntryUIStore();

    const isJjseaUser =
      authSessionStore.isJJSEA || authSessionStore.user?.isJJSEA;
    const showGeneralCategory = hasParentAccess(authSessionStore.user);

    const reportingYearOrgIds = useMemo(() => {
      if (!batchTemplateYear || !batchTemplateOrganizationOptions.length) {
        return null;
      }
      return new Set(batchTemplateOrganizationOptions.map(normalizeOrgId));
    }, [batchTemplateYear, batchTemplateOrganizationOptions]);

    const availableOrgTree = useMemo(() => {
      if (!reportingYearOrgIds) return filteredOrgTree;
      return filterOrgTreeByIds(filteredOrgTree, reportingYearOrgIds);
    }, [filteredOrgTree, reportingYearOrgIds]);

    const selectableOrgIds = useMemo(
      () => collectSelectableOrgIds(availableOrgTree),
      [availableOrgTree],
    );

    const categoryOptions = useMemo(
      () =>
        (showGeneralCategory
          ? CATEGORIES
          : CATEGORIES.filter((c) => c !== "General")
        ).map((c) => ({
          key: c,
          label: t(c),
        })),
      [showGeneralCategory, t],
    );
    const scopedCategoryOptions = useMemo(() => {
      const availableCategoryKeys = new Set(
        batchTemplateCategoryOptions.map((option) => String(option.key)),
      );
      return categoryOptions.filter((option) =>
        availableCategoryKeys.has(String(option.key)),
      );
    }, [batchTemplateCategoryOptions, categoryOptions]);
    const scopedFrameworkOptions = useMemo(() => {
      const frameworkNameByCode = new Map(
        frameworks.map((framework) => [framework.code, framework.name]),
      );
      const options = batchTemplateFrameworkOptions.map((option) => ({
        ...option,
        label: frameworkNameByCode.get(option.key) || option.label,
      }));

      if (
        isJjseaUser &&
        !options.some((option) => option.key === JJ_GROUP_FRAMEWORK_CODE)
      ) {
        options.unshift({
          key: JJ_GROUP_FRAMEWORK_CODE,
          label:
            frameworkNameByCode.get(JJ_GROUP_FRAMEWORK_CODE) ||
            JJ_GROUP_FRAMEWORK_CODE,
        });
      }

      return options;
    }, [batchTemplateFrameworkOptions, frameworks, isJjseaUser]);

    useEffect(() => {
      if (!isJjseaUser || batchTemplateFramework) return;
      setBatchTemplateFramework(JJ_GROUP_FRAMEWORK_CODE);
    }, [batchTemplateFramework, isJjseaUser, setBatchTemplateFramework]);

    useEffect(() => {
      if (!batchTemplateOrganizationId || !selectableOrgIds.length) return;
      const selectableSet = new Set(selectableOrgIds.map(normalizeOrgId));
      if (!selectableSet.has(normalizeOrgId(batchTemplateOrganizationId))) {
        setBatchTemplateOrganizationId("");
      }
    }, [
      batchTemplateOrganizationId,
      selectableOrgIds,
      setBatchTemplateOrganizationId,
    ]);

    useEffect(() => {
      loadBatchTemplateFilterOptions();
    }, [
      batchTemplateType,
      batchTemplateOrganizationId,
      batchTemplateYear,
      batchTemplateFramework,
      batchTemplateCategory,
    ]);

    useEffect(() => {
      if (scopedFrameworkOptions.length !== 1) return;
      const onlyFramework = scopedFrameworkOptions[0]?.key;
      if (onlyFramework && batchTemplateFramework !== onlyFramework) {
        setBatchTemplateFramework(onlyFramework);
      }
    }, [
      batchTemplateFramework,
      scopedFrameworkOptions,
      setBatchTemplateFramework,
    ]);

    const canDownloadTemplate =
      !downloadingTemplate &&
      !loadingDisclosures &&
      batchTemplateOrganizationId &&
      batchTemplateYear &&
      batchTemplateCategory &&
      batchTemplateFramework;
    const frameworkPlaceholder = loadingBatchTemplateFrameworks
      ? t("Loading frameworks...")
      : !batchTemplateYear
        ? t("Select reporting period first")
        : !batchTemplateOrganizationId
          ? t("Select organization first")
          : t("Select Framework");
    const categoryPlaceholder = loadingBatchTemplateCategories
      ? t("Loading categories...")
      : !batchTemplateFramework
        ? t("Select framework first")
        : t("Select Category");

    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-foreground mb-2">
            {t("Step 1: Download Template")}
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            {t("Choose template type before downloading")}
          </p>

          <div className="space-y-2">
            <label className="flex items-start gap-2 rounded-md border border-border p-3 cursor-pointer hover:border-primary/40">
              <input
                type="radio"
                name="batch-template-type"
                value="without_data"
                checked={batchTemplateType === "without_data"}
                onChange={(e) => setBatchTemplateType(e.target.value)}
                className="mt-0.5"
              />
              <span className="space-y-1">
                <span className="block text-sm font-medium text-foreground">
                  {t("Download Without Data (Blank Template)")}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t(
                    "Downloads a blank template with headers only. Use this for entering entirely new records from scratch.",
                  )}
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 rounded-md border border-border p-3 cursor-pointer hover:border-primary/40">
              <input
                type="radio"
                name="batch-template-type"
                value="with_data"
                checked={batchTemplateType === "with_data"}
                onChange={(e) => setBatchTemplateType(e.target.value)}
                className="mt-0.5"
              />
              <span className="space-y-1">
                <span className="block text-sm font-medium text-foreground">
                  {t("Download With Data (Pre-populated Template)")}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t(
                    "Downloads a template pre-populated with existing records. Use this to perform batch modifications or append new rows alongside existing data.",
                  )}
                </span>
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-3">
            <SelectField
              label={t("Reporting Period")}
              required
              options={batchTemplateYearOptions}
              value={batchTemplateYear}
              onValueChange={setBatchTemplateYear}
              placeholder={
                loadingBatchTemplateYears
                  ? t("Loading reporting periods...")
                  : t("Select Reporting Period")
              }
              disabled={loadingBatchTemplateYears}
              sortOptions={false}
              emptyText={t("No reporting periods available.")}
              emptyLabel={t("No reporting periods available")}
            />
            <div className="space-y-2">
              <Label>
                {t("Organization")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <OrgTreeSelect
                tree={availableOrgTree}
                value={batchTemplateOrganizationId}
                onChange={setBatchTemplateOrganizationId}
                placeholder={
                  batchTemplateYear
                    ? t("Select Organization")
                    : t("Select reporting period first")
                }
                showAllOption={false}
                helperText=""
                allowOrgs={
                  batchTemplateYear && selectableOrgIds.length
                    ? selectableOrgIds
                    : batchTemplateYear
                      ? ["__none__"]
                      : []
                }
                disabled={!batchTemplateYear}
                className="w-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-3">
            <SelectField
              label={t("Framework")}
              required
              options={scopedFrameworkOptions}
              value={batchTemplateFramework}
              onValueChange={setBatchTemplateFramework}
              placeholder={frameworkPlaceholder}
              disabled={
                !batchTemplateOrganizationId ||
                !batchTemplateYear ||
                loadingBatchTemplateFrameworks
              }
              emptyLabel={t("No frameworks available")}
              sortOptions={false}
            />
            <SelectField
              label={t("Category")}
              required
              options={scopedCategoryOptions}
              value={batchTemplateCategory}
              onValueChange={setBatchTemplateCategory}
              placeholder={categoryPlaceholder}
              disabled={
                !batchTemplateOrganizationId ||
                !batchTemplateYear ||
                !batchTemplateFramework ||
                loadingBatchTemplateCategories
              }
              emptyLabel={t("No categories available")}
              sortOptions={false}
            />
          </div>
          <div className="flex items-center justify-between gap-3 mt-4">
            {!canDownloadTemplate && !downloadingTemplate && (
              <p className="text-xs text-muted-foreground">
                {loadingDisclosures
                  ? t("Loading...")
                  : t("Select all fields above to enable download")}
              </p>
            )}
            <Button
              onClick={downloadBatchUploadTemplate}
              disabled={!canDownloadTemplate}
              className="gap-1.5 shrink-0 ml-auto"
            >
              {downloadingTemplate ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {downloadingTemplate
                ? t("Downloading...")
                : t("Download Template")}
            </Button>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground mb-2">
            {t("Step 2: Upload Filled Template")}
          </p>
          <div
            onClick={() => !parsingFile && fileInputRef.current?.click()}
            onDragOver={onDragOver}
            onDrop={onDrop}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 transition-colors ${
              parsingFile
                ? "border-primary bg-primary/5 pointer-events-none"
                : "border-border bg-muted/20 hover:border-primary/50 hover:bg-muted/40"
            }`}
          >
            {parsingFile ? (
              <>
                <Loader2 className="h-6 w-6 text-primary animate-spin mb-1.5" />
                <p className="text-sm font-medium text-foreground">
                  {t("Parsing file...")}
                </p>
              </>
            ) : (
              <>
                <UploadCloud className="h-6 w-6 text-muted-foreground mb-1.5" />
                <p className="text-sm font-medium text-foreground">
                  {t("Drag & drop your .xlsx file here")}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("or click to browse")}
                </p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={onFileChange}
          />
        </div>
      </div>
    );
  },
);

export default UploadForm;
